/**
 * What one external system reports about an order or line. `status` is that system's own vocabulary,
 * never branched on directly; no row (never reported) differs from a row reporting nothing.
 */
export interface ExternalStatus {
  id: string;
  /** CIN7 | SPS | TOE | 3PL */
  system: string;
  status?: string;
  /** Everything else that system sends; reference only, shape varies by system. */
  metadata: Record<string, unknown>;
  /** When we observed the status. A stale status with a fresh lastSyncedAt = an order that hasn't moved. */
  statusObservedAt?: string;
  /** When we last heard from that system at all; stale = a dead feed. */
  lastSyncedAt?: string;
  /** Our location, resolved from that system's own key, so systems compare by id. Order-level statuses only. */
  locationId?: string;
  locationName?: string;
  createdAt: string;
  updatedAt: string;
}

/** One system's key for an order: its own id (exact) and/or the number it quotes (#1006, SO-00009, a PO; not unique). */
export interface ExternalReference {
  /** CIN7 | SHOPIFY | … */
  source: string;
  externalId?: string;
  externalReference?: string;
  createdAt: string;
}

export interface OrderLine {
  id: string;
  sku: string;
  orderedQuantity?: number;
  /** List price per unit. */
  unitPrice?: number;
  /** Paid for the whole line, after any discount. */
  total?: number;
  /** From TOE / SPS / 3PL only; Cin7 reports no line-level status. */
  externalStatuses: ExternalStatus[];
  createdAt: string;
  updatedAt: string;
}

/**
 * The retailer's order, above one or more Cin7 sales. TOE reports at this level, so its status
 * describes the whole retailer order, not any single sale beneath it.
 */
export interface CustomerOrder {
  id: string;
  reference: string;
  customerId: string;
  customerName?: string;
  /** The customer's Cin7 tags (EDIPartner, JBHIFI, Amazon …). */
  customerTags: string[];
  externalStatuses: ExternalStatus[];
}

/** An order from our Postgres store, with its lines and every system's statuses. */
export interface Order {
  id: string;
  orderNumber: string;
  /** Business date from the source system; what SLA / stuck-order rules measure from. */
  orderDate?: string;
  /** Where the order originated (with externalId); absent for an order created in subemo. */
  source?: string;
  externalId?: string;
  /** Every system's key for this order, oldest first (e.g. a Shopify order's Cin7 sale). */
  externalReferences: ExternalReference[];
  lines: OrderLine[];
  /** What each system says about this sale: Cin7, and the 3PL that moved it. */
  externalStatuses: ExternalStatus[];
  /** The retailer order above it; absent only for orders imported before the parent existed. */
  customerOrder?: CustomerOrder;
  createdAt: string;
  updatedAt: string;
  /** Last successful sync with the originating system. */
  lastSyncedAt?: string;
  /** The status we own, calculated by the backend from the business rules. */
  status: OrderStatus;
}

/** The backend's OrderStatus values (domain/order/models/orderStatus.ts), in lifecycle order; sort by this. */
export const ORDER_STATUSES = [
  "Backordered",
  "Pending Allocation",
  "Allocated to 3PL",
  "Shipped",
  "Completed",
  "Shortfall",
  "Hold",
  "Cancelled",
  "Test",
  "Unknown",
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];
